import { Component, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TutorialService } from '../../services/tutorial.service';
import { AuthService } from '../../auth/services/auth.service';

@Component({
  selector: 'app-tutorial-welcome-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
  ],
  template: `
    <p-dialog
      [visible]="visible()"
      (visibleChange)="visible.set($event)"
      [modal]="true"
      [closable]="true"
      [closeOnEscape]="true"
      [blockScroll]="true"
      [style]="{ width: '550px' }"
      [contentStyle]="{ padding: '0' }"
      (onHide)="handleClose()"
    >
      <ng-template pTemplate="header">
        <div class="welcome-header">
          <div class="welcome-icon">
            <i class="pi pi-graduation-cap"></i>
          </div>
          <h2>Welcome to the ICS Referral Hub!</h2>
        </div>
      </ng-template>

      <div class="welcome-content">
        <p class="welcome-intro">
          It looks like this is your first time using the system.
          Would you like to take a quick tour to learn how to use the referral workflow?
        </p>

        <div class="benefits-section">
          <h3>What You'll Learn ({{ estimatedMinutes() }} minutes):</h3>
          <ul class="benefits-list">
            @for (benefit of benefits(); track benefit) {
              <li>
                <i class="pi pi-check-circle"></i>
                <span>{{ benefit }}</span>
              </li>
            }
          </ul>
        </div>

        <div class="info-box">
          <i class="pi pi-info-circle"></i>
          <p>
            The tour will guide you through multiple screens with sample data.
            You can exit anytime and resume later if needed.
          </p>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="welcome-footer">
          <p-button
            label="Skip Tour"
            icon="pi pi-times"
            [outlined]="true"
            (onClick)="handleSkip()"
          />
          <p-button
            label="Start Tour"
            icon="pi pi-arrow-right"
            iconPos="right"
            (onClick)="handleStart()"
          />
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      .welcome-header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 8px 0;
      }

      .welcome-icon {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: linear-gradient(135deg, #0b738e 0%, #0b435b 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 28px;
      }

      .welcome-header h2 {
        margin: 0;
        font-family: 'Open Sans', sans-serif;
        font-size: 24px;
        font-weight: 700;
        color: #0b435b;
        text-align: center;
      }

      .welcome-content {
        padding: 24px;
      }

      .welcome-intro {
        font-family: 'Open Sans', sans-serif;
        font-size: 16px;
        line-height: 1.6;
        color: #374151;
        margin: 0 0 24px 0;
        text-align: center;
      }

      .benefits-section {
        background: #f0f9ff;
        border: 1px solid #bae6fd;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
      }

      .benefits-section h3 {
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: 700;
        color: #0b738e;
        margin: 0 0 12px 0;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .benefits-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .benefits-list li {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 10px;
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        color: #374151;
        line-height: 1.5;
      }

      .benefits-list li:last-child {
        margin-bottom: 0;
      }

      .benefits-list li i {
        color: #059669;
        font-size: 18px;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .benefits-list li span {
        flex: 1;
      }

      .info-box {
        display: flex;
        gap: 12px;
        background: #fef3c7;
        border: 1px solid #fde68a;
        border-radius: 6px;
        padding: 12px;
        margin-bottom: 20px;
      }

      .info-box i {
        color: #d97706;
        font-size: 20px;
        flex-shrink: 0;
      }

      .info-box p {
        margin: 0;
        font-family: 'Open Sans', sans-serif;
        font-size: 13px;
        color: #78350f;
        line-height: 1.5;
      }

      .welcome-footer {
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        padding: 16px 24px;
        border-top: 1px solid #e5e7eb;
        background: #f8fcfd;
      }

      @media (max-width: 600px) {
        p-dialog [style*='width'] {
          width: 90vw !important;
        }

        .welcome-footer {
          flex-direction: column-reverse;
        }

        .welcome-footer p-button {
          width: 100%;
        }
      }
    `,
  ],
})
export class TutorialWelcomeModalComponent {
  private tutorialService = inject(TutorialService);
  private authService = inject(AuthService);
  private closeAction: 'none' | 'dismiss' | 'start' = 'none';

  /** Whether the dialog is visible */
  visible = signal(false);

  /** Estimated tutorial duration based on user role */
  estimatedMinutes = computed(() => {
    const user = this.authService.currentUser();
    if (!user?.groups) return 5;

    if (user.groups.includes('hospital_users')) {
      return 8;
    }
    return 7;
  });

  /** Benefits to display based on user role */
  benefits = computed(() => {
    const user = this.authService.currentUser();
    if (!user?.groups) return [];

    const isHospitalUser = user.groups.includes('hospital_users');

    return isHospitalUser
      ? [
          'Understanding the referral workflow and status meanings',
          'Creating complete referrals that get approved faster',
          'Using AI-powered document extraction',
          'Tracking referral status and responding to requests',
        ]
      : [
          'Prioritizing and managing your referral queue efficiently',
          'Reviewing clinical documentation for eligibility',
          'Making informed approval decisions',
          'Communicating effectively with hospital staff',
        ];
  });

  /** Show the welcome modal */
  show(): void {
    this.closeAction = 'none';
    this.visible.set(true);
  }

  /** Hide the welcome modal */
  hide(): void {
    this.visible.set(false);
  }

  /** Handle Start Tour button click */
  handleStart(): void {
    this.closeAction = 'start';
    this.hide();
    // Start the comprehensive tutorial
    this.tutorialService.startComprehensiveTutorial();
  }

  /** Handle Skip Tour button click */
  handleSkip(): void {
    this.closeAction = 'dismiss';
    this.tutorialService.dismissTutorial();
    this.hide();
  }

  /** Handle dialog close (X button or escape) — called from (onHide), dialog is already hiding */
  handleClose(): void {
    if (this.closeAction !== 'none') {
      this.closeAction = 'none';
      return;
    }

    this.tutorialService.dismissTutorial();
  }
}
